import { queryClientKeys } from "$root/third-party-integrations/react-query/typedQueryClient";
import { useQuery, type UseQueryResult } from "@tanstack/react-query";

/**
 * A build's identity, as baked into the `<html>` tag of `index.html` at build time:
 * `<html data-version="%VITE_APP_VERSION%" … data-sha="%VITE_APP_SHA%">`. Vite substitutes the
 * placeholders (see `vite.config.js` / `.env`). In dev the served HTML keeps the literal
 * placeholders, which is why the check is disabled on localhost (see `isLocalhost`).
 */
export type BuildIdentity = {
	/** package.json version — human-meaningful, but only bumps on release. */
	version: string | null;
	/** Short commit SHA — changes on every deploy, so it also covers staging re-deploys. */
	sha: string | null;
};

/** The build identity this browser tab originally loaded. */
const loaded: BuildIdentity = {
	version: document.documentElement.dataset.version ?? null,
	sha: document.documentElement.dataset.sha ?? null,
};

/**
 * Disable the mechanism on localhost: the dev server serves the unsubstituted
 * `%VITE_APP_*%` placeholders, so there is nothing meaningful to compare against.
 */
const isLocalhost = /^(localhost|127\.0\.0\.1|\[::1\])$/.test(window.location.hostname);

/** Backstop polling cadence. Window-focus refetch is the primary trigger (laptop wake-from-suspend). */
const pollIntervalMs = 5 * 60 * 1000;

export type AppVersionCheck = {
	/** Build identity this tab is running. */
	loaded: BuildIdentity;
	/** Build identity currently served by the origin. */
	latest: BuildIdentity;
	/** True once the deployed build differs from the one this tab loaded. */
	updateAvailable: boolean;
};

/**
 * Extracts a `data-*` attribute of the `<html>` opening tag from a document's HTML source.
 *
 * Anchored to the `<html>` tag on purpose: `[^>]*` can't cross the tag's closing `>`, and `\b`
 * avoids matching a `…${attr}` suffix on some other attribute — so a stray occurrence in an
 * unrelated tag, attribute, or HTML comment can't be mistaken for the build identity.
 *
 * @returns the attribute value, or `null` if the `<html>` tag doesn't carry it.
 */
export function parseHtmlTagAttr(html: string, attr: "data-version" | "data-sha"): string | null {
	return html.match(new RegExp(`<html\\b[^>]*\\b${attr}="([^"]+)"`, "i"))?.[1] ?? null;
}

/** Parses the full build identity (version + sha) from a document's HTML source. */
export function parseBuildIdentity(html: string): BuildIdentity {
	return {
		version: parseHtmlTagAttr(html, "data-version"),
		sha: parseHtmlTagAttr(html, "data-sha"),
	};
}

/** A build attribute is comparable only if it's present and not the `"unknown"` build-time fallback. */
function isMeaningful(value: string | null): value is string {
	return value != null && value !== "unknown";
}

/**
 * Whether `latest` is a different build than `loaded`.
 *
 * Prefers the commit SHA — it changes on every deploy, so it detects staging re-deploys where the
 * human version number is left unchanged. Falls back to the version number when either build didn't
 * expose a usable SHA (e.g. git unavailable at build → `"unknown"`), and reports no update when
 * neither identifier is comparable (rather than a false positive).
 */
export function isUpdateAvailable(loadedBuild: BuildIdentity, latestBuild: BuildIdentity): boolean {
	if (isMeaningful(loadedBuild.sha) && isMeaningful(latestBuild.sha)) {
		return loadedBuild.sha !== latestBuild.sha;
	}
	if (isMeaningful(loadedBuild.version) && isMeaningful(latestBuild.version)) {
		return loadedBuild.version !== latestBuild.version;
	}
	return false;
}

async function fetchDeployedVersion(): Promise<AppVersionCheck> {
	// index.html is served with `Cache-Control: no-cache`, so a plain fetch always revalidates
	// with the origin and never returns a stale body — no cache-busting query param needed.
	const res = await fetch(`${window.location.origin}/`);
	const html = await res.text();
	const latest = parseBuildIdentity(html);

	return {
		loaded,
		latest,
		updateAvailable: isUpdateAvailable(loaded, latest),
	};
}

/**
 * Polls the deployed `index.html` and reports whether a newer version of Sphere has shipped
 * since this tab was opened. Non-destructive: it only surfaces an indicator/toast, never reloads.
 *
 * React Query gives us focus-refetch, interval polling and latching for free:
 * - `refetchOnWindowFocus` catches the common case of a laptop waking from suspend.
 * - `refetchInterval` polls as a backstop, and returns `false` once an update is found (latch).
 * - `retry: false` keeps failures silent (offline suspend etc.).
 *
 * The query is deduped by key, so calling this hook from multiple places (drawer badge + toast
 * trigger) results in a single poll shared across all subscribers.
 */
export function useAppVersionCheck(): UseQueryResult<AppVersionCheck> {
	return useQuery({
		queryKey: [queryClientKeys.appVersionCheck],
		queryFn: fetchDeployedVersion,
		enabled: !isLocalhost,
		refetchInterval: (data) => (data?.updateAvailable ? false : pollIntervalMs),
		refetchOnWindowFocus: true, // global default is `false` (see typedQueryClient.ts)
		refetchIntervalInBackground: false,
		retry: false,
		staleTime: 0,
	});
}
